/**
 * PublicCardGrid — premium card grid layout for public list pages.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import envConfig from '../../config/envConfig';
import { getCurrencyDisplay } from '../../utils/currencies';
import { localeDateIn, parseInstant, toDisplayString, zoneOf } from '../../utils/utils';
import JsonCell from '../common/JsonCell';
import PublicTagBadges from './PublicTagBadges';

const isJsonField = (f) => !!f && (f.data_type === 'json' || f.data_type === 'jsonb');

function resolveFileUrl(value) {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${envConfig.fileServerUrl}/public/${value}`;
}

export const COL_CLASS = {
  '2': 'col-12 col-sm-6',
  '3': 'col-12 col-sm-6 col-md-4',
  '4': 'col-6 col-md-3',
};

export const DEFAULT_COL_CLASS = COL_CLASS['3'];

const BADGE_COLOR_MAP = {
  primary: '#2d6cf6', success: '#16a34a', warning: '#d97706',
  danger: '#dc2626', info: '#0891b2', secondary: '#64748b',
};

const TITLE_LIMIT = 60;
const SUBTITLE_LIMIT = 80;
const DESC_LIMIT = 120;

function trunc(val, limit) {
  const s = val == null ? '' : String(val);
  return s.length > limit ? s.slice(0, limit) + '…' : s;
}

function formatDate(val, timeZone) {
  try {
    // Read as the instant it names, not as the viewer's wall clock — see
    // parseInstant in utils.js. The card and the record's own detail page
    // must not disagree about which day an event falls on.
    const d = parseInstant(val);
    if (!d) return String(val);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays > 1 && diffDays < 7) return `${diffDays}d ago`;
    return localeDateIn(d, { year: 'numeric', month: 'short', day: 'numeric' }, timeZone);
  } catch {
    return String(val);
  }
}

export default function PublicCardGrid({ records = [], fields = [], mapping = {}, layoutConfig = {}, detailEnabled = true, detailButtonLabel = '', tableSlug: tableSlugProp }) {
  const navigate = useNavigate();
  const { tableSlug: tableSlugParam } = useParams();
  const tableSlug = tableSlugProp || tableSlugParam;
  const { theme } = useTheme();

  const badgeColors = mapping.badge_colors || {};
  const accentColor = theme.primary;
  const colClass = COL_CLASS[String(layoutConfig.cards_per_row)] || COL_CLASS['3'];

  const fieldMap = Object.fromEntries(fields.map(f => [f.name, f]));
  const imageMappedMeta = mapping.image_field ? fieldMap[mapping.image_field] : null;
  const isRealImageField = imageMappedMeta?.data_type === 'image';

  const css = `
    @keyframes pcShine {
      0%   { transform: translateX(-180%) skewX(-15deg); opacity: 0; }
      8%   { opacity: 1; }
      92%  { opacity: 1; }
      100% { transform: translateX(320%) skewX(-15deg); opacity: 0; }
    }
    @keyframes pcFadeUp {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .pc-card {
      background: #ffffff;
      border-radius: 16px;
      overflow: hidden;
      border: none;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
      transition: box-shadow 0.2s ease, transform 0.2s ease;
      display: flex;
      flex-direction: column;
      height: 100%;
      position: relative;
      animation: pcFadeUp 0.35s ease both;
    }
    .pc-card:hover {
      box-shadow: 0 8px 16px rgba(0,0,0,0.12);
      transform: translateY(-4px);
    }
    .pc-card:active {
      transform: translateY(0) scale(0.994);
      transition-duration: 0.1s;
    }

    /* ── Cover ── */
    .pc-cover {
      position: relative;
      height: 220px;
      overflow: hidden;
      flex-shrink: 0;
      background: linear-gradient(145deg, ${accentColor}18 0%, ${accentColor}38 100%);
    }
    .pc-cover::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 36%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255,255,255,0.30) 50%,
        transparent 100%
      );
      transform: translateX(-180%) skewX(-15deg);
      pointer-events: none;
      opacity: 0;
      z-index: 5;
    }
    .pc-card:hover .pc-cover::after {
      animation: pcShine 0.68s cubic-bezier(0.4,0,0.2,1) forwards;
    }

    .pc-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      opacity: 0;
      transition: opacity 0.42s ease, transform 0.60s cubic-bezier(0.4,0,0.2,1);
    }
    .pc-img.loaded { opacity: 1; }
    .pc-card:hover .pc-img { transform: scale(1.05); }

    .pc-img-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to bottom,
        transparent 35%,
        rgba(0,0,0,0.05) 60%,
        rgba(0,0,0,0.32) 100%
      );
      opacity: 0;
      transition: opacity 0.28s ease;
      pointer-events: none;
      z-index: 2;
    }
    .pc-card:hover .pc-img-overlay { opacity: 1; }

    .pc-img-title {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 10px 16px 14px;
      color: #fff;
      font-size: 0.80rem;
      font-weight: 700;
      text-shadow: 0 1px 6px rgba(0,0,0,0.65);
      opacity: 0;
      transform: translateY(8px);
      transition: opacity 0.24s ease, transform 0.24s ease;
      pointer-events: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      z-index: 3;
    }
    .pc-card:hover .pc-img-title { opacity: 1; transform: translateY(0); }

    /* ── Body ── */
    .pc-body {
      padding: 1.15rem 1.35rem 1.05rem;
      display: flex;
      flex-direction: column;
      flex: 1;
      background: transparent;
    }

    .pc-title {
      font-weight: 700;
      font-size: 1.04rem;
      line-height: 1.35;
      color: #0f172a;
      margin: 0 0 5px;
      letter-spacing: -0.014em;
    }

    .pc-subtitle {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.785rem;
      color: #334155;
      font-weight: 500;
      margin: 0 0 8px;
    }
    .pc-subtitle-dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: ${accentColor};
      flex-shrink: 0;
      opacity: 0.80;
    }

    .pc-desc {
      font-size: 0.795rem;
      color: #475569;
      line-height: 1.6;
      flex-grow: 1;
      margin: 0 0 12px;
    }

    /* ── Footer ── */
    .pc-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: auto;
      padding-top: 10px;
      border-top: 1px solid ${accentColor}18;
    }

    .pc-date {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 0.69rem;
      color: #64748b;
      font-weight: 500;
      background: ${accentColor}0d;
      padding: 3px 10px;
      border-radius: 999px;
      border: 1px solid ${accentColor}18;
    }
    .pc-date i { font-size: 0.60rem; }

    .pc-cost {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 0.72rem;
      color: #16a34a;
      font-weight: 700;
      background: #16a34a0d;
      padding: 3px 10px;
      border-radius: 999px;
      border: 1px solid #16a34a28;
    }
    .pc-cost i { font-size: 0.60rem; }

    .pc-cta {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 0.72rem;
      font-weight: 700;
      color: #fff;
      background: linear-gradient(135deg, ${accentColor} 0%, ${accentColor}d8 100%);
      border-radius: 999px;
      padding: 6px 15px;
      box-shadow: 0 2px 8px ${accentColor}3c, 0 1px 3px ${accentColor}24;
      transition: box-shadow 0.2s ease, transform 0.2s ease, opacity 0.15s ease;
      letter-spacing: 0.01em;
    }
    .pc-cta:hover {
      box-shadow: 0 4px 18px ${accentColor}58, 0 2px 6px ${accentColor}30;
      transform: translateY(-1px) scale(1.04);
      opacity: 0.93;
    }
    .pc-cta i {
      font-size: 0.65rem;
      transition: transform 0.17s ease;
    }
    .pc-cta:hover i { transform: translateX(2px); }

    /* ── Accent stripe ── */
    .pc-stripe {
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: ${theme.gradientBackground};
      z-index: 4;
    }

    /* ── Badge ── */
    .pc-badge {
      position: absolute;
      top: 12px; right: 12px;
      z-index: 5;
      color: #fff;
      font-size: 0.585rem;
      font-weight: 800;
      padding: 4px 10px;
      border-radius: 999px;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      backdrop-filter: blur(6px);
      box-shadow: 0 2px 8px rgba(0,0,0,0.18);
    }

    /* ── No-image placeholder ── */
    .pc-placeholder {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .pc-placeholder-bg {
      position: absolute;
      inset: 0;
    }
    .pc-placeholder-letter {
      position: relative;
      font-size: clamp(52px, 10vw, 80px);
      font-weight: 900;
      color: ${accentColor};
      opacity: 0.18;
      user-select: none;
      letter-spacing: -0.04em;
      line-height: 1;
      text-transform: uppercase;
    }

    /* ── Empty state ── */
    .pc-empty {
      text-align: center;
      padding: 5rem 2rem;
      background: #fff;
      border-radius: 18px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.06), 0 8px 30px ${accentColor}14;
      border: 1.5px solid ${accentColor}22;
    }
    .pc-empty-icon {
      width: 78px;
      height: 78px;
      border-radius: 50%;
      background: linear-gradient(135deg, ${accentColor}12 0%, ${accentColor}28 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 18px;
      box-shadow: 0 4px 22px ${accentColor}1c;
    }
  `;

  if (records.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div className="pc-empty">
          <div className="pc-empty-icon">
            <i className="bi bi-grid-3x3-gap" style={{ fontSize: 30, color: accentColor, opacity: 0.65 }} />
          </div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', marginBottom: 8 }}>
            No records found
          </div>
          <p style={{ color: '#94a3b8', fontSize: '0.84rem', margin: '0 auto', maxWidth: 280 }}>
            Try adjusting your filters or search query
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="row g-4 g-md-4" style={{ rowGap: '1.75rem' }}>
        {records.map((record) => {
          const title = record[mapping.title_field] ?? null;
          const subtitle = mapping.subtitle_field ? record[mapping.subtitle_field] : null;
          const imageVal = (isRealImageField && mapping.image_field) ? record[mapping.image_field] : null;
          const desc = mapping.description_field ? record[mapping.description_field] : null;
          const tags = mapping.tags_field ? record[mapping.tags_field] : null;

          const titleStr = (title != null && title !== '') ? toDisplayString(title) : '';
          const subtitleStr = (subtitle != null && subtitle !== '') ? toDisplayString(subtitle) : null;
          const descStr = (desc != null && desc !== '') ? toDisplayString(desc) : null;
          const tagsStr = (tags != null && tags !== '') ? String(tags) : null;
          const subtitleIsJson = isJsonField(fieldMap[mapping.subtitle_field]);
          const descIsJson = isJsonField(fieldMap[mapping.description_field]);
          const badge = mapping.badge_field ? record[mapping.badge_field] : null;
          const dateVal = mapping.date_field ? record[mapping.date_field] : null;
          const costVal = mapping.cost_field ? record[mapping.cost_field] : null;

          const badgeKey = badge ? (badgeColors[badge] || 'primary') : 'primary';
          const badgeBg = BADGE_COLOR_MAP[badgeKey] || BADGE_COLOR_MAP.primary;

          return (
            <div key={record.id} className={colClass}>
              <div
                className="pc-card arcee-pub--card"
                data-record-id={record.id}
                style={{ cursor: detailEnabled ? 'pointer' : 'default' }}
                onClick={detailEnabled ? () => navigate(`/p/${tableSlug}/${record.id}`) : undefined}
              >
                {/* ── Cover ── */}
                <div className="pc-cover arcee-pub__thumb">
                  <div className="pc-stripe" />

                  {imageVal ? (
                    <>
                      <img
                        src={resolveFileUrl(imageVal)}
                        alt={titleStr || 'cover'}
                        className="pc-img"
                        loading="lazy"
                        onLoad={e => e.target.classList.add('loaded')}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                      <div className="pc-img-overlay" />
                      {titleStr && (
                        <div className="pc-img-title">{trunc(titleStr, TITLE_LIMIT)}</div>
                      )}
                    </>
                  ) : (
                    <div className="pc-placeholder">
                      <div className="pc-placeholder-bg">
                        <div style={{
                          position: 'absolute', inset: 0,
                          backgroundImage: `
                            radial-gradient(ellipse at 15% 88%, ${accentColor}52 0%, transparent 52%),
                            radial-gradient(ellipse at 88% 12%, ${accentColor}38 0%, transparent 52%),
                            radial-gradient(ellipse at 50% 50%, ${accentColor}1e 0%, transparent 68%)
                          `,
                        }} />
                        <div style={{
                          position: 'absolute', inset: 0,
                          backgroundImage: `radial-gradient(circle, ${accentColor}1e 1.5px, transparent 1.5px)`,
                          backgroundSize: '24px 24px',
                        }} />
                      </div>
                      <img
                        src="/favicon.svg"
                        alt="Arcee"
                        style={{ position: 'relative', width: 56, height: 56, opacity: 0.45, userSelect: 'none' }}
                      />
                    </div>
                  )}

                  {/* Badge */}
                  {badge && (
                    <span
                      className="pc-badge arcee-pub__badge"
                      style={{ background: badgeBg, boxShadow: `0 2px 12px ${badgeBg}52` }}
                    >
                      {badge}
                    </span>
                  )}

                  {/* Bottom scrim */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: 68,
                    background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.16))',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }} />
                </div>

                {/* ── Body ── */}
                <div className="pc-body">
                  {titleStr && (
                    <div
                      className="pc-title arcee-pub__title"
                      title={titleStr.length > TITLE_LIMIT ? titleStr : undefined}
                    >
                      {trunc(titleStr, TITLE_LIMIT)}
                    </div>
                  )}

                  {subtitleStr && (
                    <div
                      className="pc-subtitle arcee-pub__subtitle"
                      title={!subtitleIsJson && subtitleStr.length > SUBTITLE_LIMIT ? subtitleStr : undefined}
                    >
                      <span className="pc-subtitle-dot" />
                      {subtitleIsJson ? <JsonCell value={subtitle} /> : trunc(subtitleStr, SUBTITLE_LIMIT)}
                    </div>
                  )}

                  {descStr && (
                    <div
                      className="pc-desc arcee-pub__desc"
                      title={!descIsJson && descStr.length > DESC_LIMIT ? descStr : undefined}
                    >
                      {descIsJson ? <JsonCell value={desc} /> : trunc(descStr, DESC_LIMIT)}
                    </div>
                  )}

                  {tagsStr && (
                    <div style={{ marginBottom: 10 }}>
                      <PublicTagBadges value={tagsStr} bgColor={mapping.tag_bg_color} accentColor={accentColor} />
                    </div>
                  )}

                  {/* ── Footer ── */}
                  <div className="pc-footer">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {dateVal && (
                        <span className="pc-date arcee-pub__date">
                          <i className="bi bi-calendar3" />
                          {formatDate(dateVal, zoneOf(record))}
                        </span>
                      )}
                      {costVal != null && costVal !== '' && (
                        <span className="pc-cost arcee-pub__cost">
                          <span style={{ fontWeight: 700 }}>{getCurrencyDisplay(mapping.cost_currency)}</span>
                          {costVal}
                        </span>
                      )}
                    </div>

                    {detailEnabled && (
                      <span className="pc-cta arcee-pub__cta">
                        {detailButtonLabel && <span>{detailButtonLabel}</span>}
                        <i className="bi bi-arrow-right" />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
